/**
 * @description renders a dropdown menu of links in the navigation section of the application bar
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import Button from '@mui/material/Button';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import NavLink from './NavLink';

const styles = {
  button: {
    color: 'primary.contrastText',
    fontSize: '1em',
    fontWeight: 'bold',
    padding: 0,
    textAlign: 'left',
    textTransform: 'none',
    wordBreak: 'break-word',

    '& .MuiButton-endIcon': {
      marginLeft: 0
    }
  },
  menuItem: {
    padding: 0
  }
};

/**
 * @prop {{ label: string, path: string, reloadDocument?: boolean }[]} entries - an array of link entries to be included in the dropdown menu
 * @prop {string} label - the label of the dropdown menu
 */
const NavDropdown = ({ entries, label }) => {
  // Maintains a pointer to the menu anchor
  const [anchorEl, setAnchorEl] = React.useState(null);

  /**
   * Handles menu click events by saving the menu anchor target (which in turn will open the menu)
   * @param {ClickEvent} evt - menu click event
   */
  function handleClick (evt) {
    setAnchorEl(evt.currentTarget);
  }

  /**
   * Handles menu close events by clearing the menu anchor target (which in turn will close the menu)
   */
  function handleClose () {
    setAnchorEl(null);
  }

  return (
    <>
      <Button
        endIcon={<ArrowDropDownIcon />}
        onClick={(evt) => handleClick(evt)}
        sx={styles.button}
      >
        {label}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleClose()}
      >
        {entries.map((entry) => (
          <MenuItem
            key={entry.label}
            onClick={() => handleClose()}
            sx={styles.menuItem}
          >
            { /* eslint-disable-next-line react/jsx-props-no-spreading */ }
            <NavLink {...entry} variant='dropdown' />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

NavDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      reloadDocument: PropTypes.bool
    })
  )
};

NavDropdown.defaultProps = {
  entries: []
};

export default NavDropdown;
